import React from 'react';
import styles from './aiThinking.scss';

export interface ThinkingProps {
  /** Text to display inside the animated mask (e.g. "Thinking" or "Preparing answer...") */
  text?: string;
  /** Accessible label read by screen readers. Defaults to text or a generic phrase. */
  'aria-label'?: string;
  /** Whether to reduce motion (disables sliding gradient). If user has prefers-reduced-motion this is automatically handled by CSS. */
  reduceMotion?: boolean;
  /** Class name for custom styling overrides */
  className?: string;
  /** Optional id */
  id?: string;
}

/**
 * Thinking / loading shimmer used while an AI assistant is generating a response.
 * Visual only; no rotating / dynamic text here (that comes from API outside Storybook scope).
 */
export function AiThinking({
  text = 'Thinking…',
  'aria-label': ariaLabel,
  reduceMotion = false,
  className,
  id,
}: ThinkingProps): JSX.Element {
  const rootClass = [styles.loading, reduceMotion ? styles.reduceMotion : undefined, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      aria-label={ariaLabel || text || 'Assistant is thinking'}
      aria-live="polite"
      className={rootClass}
      data-testid="thinking"
      id={id}
      role="status"
    >
      {/* Mask overlay that reveals gradient + static base */}
      <div aria-hidden="true" className={styles.loadingOverlay}>
        <svg fill="none" height="32" viewBox="0 0 218 32" width="218" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <mask id="thinkingMask">
              <rect fill="white" height="200" width="500" x="-200" y="-100" />
              <g id="sparkle">
                <path
                  d="M14.6475 4.15137C17.2166 9.59239 21.7143 13.9263 27.2441 16.3164L28.3594 16.7988L27.7773 17.0156C21.8447 19.4165 17.0868 24.0327 14.499 29.8701L14.3691 30.1523L13.9531 29.1367C11.6493 23.6751 7.46001 19.2267 2.15332 16.5977L2 16.498L2.69336 16.1621C7.7359 13.7802 11.8205 9.76564 14.2646 4.80078L14.6475 4.15137ZM25.6621 1.60254C26.6252 3.6419 28.311 5.26638 30.3838 6.16211L30.8018 6.34277L30.584 6.42383C28.3603 7.32389 26.5763 9.05508 25.6064 11.2432L25.5586 11.3486L25.4023 10.9678C24.5387 8.92086 22.9684 7.25387 20.9795 6.26855L20.9219 6.23145L21.1816 6.10547C23.0717 5.21252 24.6026 3.70676 25.5186 1.8457L25.6621 1.60254Z"
                  fill="black"
                />
              </g>
              <text
                fill="black"
                fontFamily="Public Sans, sans-serif"
                fontSize="14"
                style={{ whiteSpace: 'pre' }}
                xmlSpace="preserve"
              >
                <tspan x="40" y="22">
                  {text}
                </tspan>
              </text>
            </mask>
          </defs>
          <rect fill="#fff" height="33" mask="url(#thinkingMask)" width="218" x="0" y="0" />
        </svg>
      </div>
      {/* Animated gradient */}
      <div aria-hidden="true" className={styles.animatedGradient} />
      {/* Static base + sparkle color block */}
      <div aria-hidden="true" className={styles.loadingStatic}>
        <svg fill="none" height="33" viewBox="0 0 218 33" width="218" xmlns="http://www.w3.org/2000/svg">
          <g>
            <g>
              <rect fill="url(#sparkleGradient)" height="29" width="29" x="2" y="2" />
            </g>
            <rect fill="#9699A0" height="22" width="178" x="40" y="5" />
          </g>
          <defs>
            <linearGradient
              gradientUnits="userSpaceOnUse"
              id="sparkleGradient"
              x1="8.18227"
              x2="86.9811"
              y1="15.0759"
              y2="-8.76355"
            >
              <stop stopColor="#FF44CB" />
              <stop offset="0.264423" stopColor="#A1CDF5" />
              <stop offset="0.659066" stopColor="#FF44CB" />
              <stop offset="1" stopColor="#A1CDF5" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
}

AiThinking.displayName = 'Thinking';

export default AiThinking;
